import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import {
    createUserAPI,
    updateUserAPI,
    getAllUsersAPI,
    addUserToServerAPI,
    sendPasswordResetAPI,
    getAdminsForServerAPI
} from '../services/serverService';
import { JOB_TITLES } from '../utils/constants';

const Spinner = () => <div className="spinner"></div>;

const AddEmployeeModal = ({ serverId, closeModal, refreshList, employeeToEdit, currentLimits, existingUsers = [] }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(employeeToEdit);

    const [view, setView] = useState(isEditMode ? 'create' : 'search'); 
    const [searchTerm, setSearchTerm] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    
    const [formData, setFormData] = useState({
        username: '',
        fullName: '',
        email: '',
        password: '',
        primaryRegionalTag: '',
        secondaryRegionalTag: '',
        jobTitle: '',
        isTravelling: '',
        phoneCode: '+44', 
        phoneNumber: '',
        assignedAdminId: '',
    });
    
    const [admins, setAdmins] = useState([]);
    const [isLoadingAdmins, setIsLoadingAdmins] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [apiError, setApiError] = useState('');

    const [resetEmailSent, setResetEmailSent] = useState(false);

    const handleSendResetEmail = async () => {
        const confirmAlert = window.confirm(`Send a password reset email to ${formData.email}?`);
        if (!confirmAlert) return;

        try {
            const targetUser = employeeToEdit;
            const targetId = targetUser.username || targetUser.id || targetUser.uid || targetUser.email;

            await sendPasswordResetAPI({
                userId: targetId,
                email: formData.email,
                username: formData.username,
                fullName: formData.fullName
            });
            
            setResetEmailSent(true);
            alert(`Password reset email sent safely to ${formData.email}!`);
        } catch (error) {
            console.error("Reset Password Error:", error);
            alert("Failed to send reset email. Please check the logs.");
        }
    };

    useEffect(() => {
        if (isEditMode && employeeToEdit) {
            setView('create');
            setFormData({
                username: employeeToEdit.username || '',
                fullName: employeeToEdit.fullName || '',
                email: employeeToEdit.email || '',
                password: '',
                primaryRegionalTag: employeeToEdit.primaryRegionalTag || '',
                secondaryRegionalTag: employeeToEdit.secondaryRegionalTag || '',
                jobTitle: employeeToEdit.jobTitle || employeeToEdit.type || '',
                isTravelling: employeeToEdit.isTravelling === true ? 'true' : employeeToEdit.isTravelling === false ? 'false' : '',
                phoneCode: employeeToEdit.phoneCode || '+44',
                phoneNumber: employeeToEdit.phoneNumber || '',
                assignedAdminId: employeeToEdit.adminID || employeeToEdit.assignedAdminId || '',
            });
        }
    }, [isEditMode, employeeToEdit]); 

    const { fullName } = formData;
    useEffect(() => {
        if (!isEditMode && view === 'create') {
            if (fullName.trim() === '') {
                setFormData(prev => ({ ...prev, username: '' }));
                return;
            }
            const nameParts = fullName.trim().split(/\s+/);
            const suggestion = nameParts.map((part, index) => {
                if (index === 0) return part.toLowerCase();
                return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
            }).join('');
            setFormData(prev => ({ ...prev, username: suggestion }));
        }
    }, [fullName, isEditMode, view]);

    useEffect(() => {
        if (!isEditMode && view === 'search') {
            const fetchUsers = async () => {
                setIsSearching(true);
                try {
                    const response = await getAllUsersAPI();
                    setAllUsers(response.data.data);
                } catch (err) {
                    setApiError("Could not load user list.");
                } finally {
                    setIsSearching(false);
                }
            };
            fetchUsers();
        }
    }, [isEditMode, view]);

    useEffect(() => {
        if (searchTerm === '') {
            setSearchResults([]);
            return;
        }
        const lowerTerm = searchTerm.toLowerCase();
        const results = allUsers.filter(user =>
            (user.username || "").toLowerCase().includes(lowerTerm) ||
            (user.fullName || "").toLowerCase().includes(lowerTerm) ||
            (user.email || "").toLowerCase().includes(lowerTerm)
        );
        setSearchResults(results);
    }, [searchTerm, allUsers]);

    useEffect(() => {
        if (view === 'create') {
            const fetchAdmins = async () => {
                setIsLoadingAdmins(true);
                try {
                    const response = await getAdminsForServerAPI(serverId);
                    setAdmins(response.data.data);
                } catch (error) {
                    console.error("Failed to fetch admins for dropdown", error);
                    setApiError("Could not load list of admins.");
                } finally {
                    setIsLoadingAdmins(false);
                }
            };
            fetchAdmins();
        }
    }, [serverId, view]);

    const handleAddExistingUser = async (user) => {
        setIsSaving(true);
        setApiError('');
        try {
            if ((user.role || 'unknown') !== 'employee') {
                setApiError(`This user is a(n) ${user.role || 'unknown'} and cannot be added as an Employee.`);
                setIsSaving(false);
                return;
            }
            await addUserToServerAPI(serverId, user.username || user.id);
            alert(`Existing employee '${user.fullName}' successfully added.`);
            refreshList();
            closeModal();
        } catch (err) {
            setApiError("Failed to add existing user.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'username') value = value.replace(/\s+/g, '');
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleSubmit = async (e, shouldRedirect = false, bypassLinkUid = null) => {
        if (e) e.preventDefault();
        setIsSaving(true);
        setApiError('');
        try {
            const payloadData = { ...formData };
            if (payloadData.isTravelling === 'true') payloadData.isTravelling = true;
            else if (payloadData.isTravelling === 'false') payloadData.isTravelling = false;
            else payloadData.isTravelling = false; // default or delete

            let newUserId = null;
            if (isEditMode) {
                // Ensure updates target the local document ID (username)
                const targetId = employeeToEdit.username || employeeToEdit.id || employeeToEdit.uid || employeeToEdit.email;
                await updateUserAPI(targetId, payloadData);
                alert('Employee updated successfully!');
            } else {
                const payload = { ...payloadData, role: 'employee', serverIds: [serverId] };
                if (bypassLinkUid) payload.forceLinkUid = bypassLinkUid;

                const response = await createUserAPI(payload);

                // INTERCEPT: Multi-Tenant Identity Linking Fork
                if (response.data && response.data.status === 'requires_linking') {
                    setIsSaving(false);
                    const proceed = window.confirm(response.data.message);
                    if (proceed) {
                        return handleSubmit(null, shouldRedirect, response.data.uid);
                    } else {
                        return; // User cancelled linking
                    }
                }

                newUserId = formData.username; 
                alert('New employee created successfully!');
            }
            refreshList();
            if (shouldRedirect && newUserId) {
                navigate(`/servers/${serverId}/employees/${newUserId}`, { state: { openEditModal: true } });
            } else {
                closeModal();
            }
        } catch (err) {
            setApiError(err.response?.data?.message || 'Operation failed.');
        } finally {
            setIsSaving(false);
        }
    };

    const isOverLimit = currentLimits && currentLimits.maxLimit > 0 && currentLimits.currentCount >= currentLimits.maxLimit;

    const renderLimitWarning = () => {
        if (!isOverLimit || isEditMode) return null;
        return (
            <div style={{ backgroundColor: '#fffff0', border: '1px solid #d69e2e', color: '#975a16', padding: '12px', borderRadius: '6px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                <FaExclamationTriangle size={18} />
                <div><strong>License Limit Reached</strong> ({currentLimits.currentCount} / {currentLimits.maxLimit} Staff Members). Adding this user will result in extra charges.</div>
            </div>
        );
    };

    const renderSearchView = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, borderBottom: '2px solid #e2e8f0', paddingBottom: '6px', fontSize: '22px', color: '#2d3748' }}>Add Employee to Server</h2>
                <button type="button" onClick={closeModal} disabled={isSaving} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: isSaving ? 'not-allowed' : 'pointer', color: isSaving ? '#cbd5e0' : '#a0aec0', padding: '0', lineHeight: '24px' }}>&times;</button>
            </div>
            {renderLimitWarning()}
            <p className="modal-subtitle">Search for an existing global employee or create a new one.</p>
            <div className="form-group full-width">
                <label>Search by Name, Email, or Username</label>
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="e.g., Jane Smith" />
            </div>
            {isSearching && <p>Loading users...</p>}
            {apiError && <p className="error-message">{apiError}</p>}
            <div className="search-results-container">
                {searchResults.length > 0 ? (
                    searchResults.map(user => (
                        <div className="search-result-item" key={user.id}>
                            <div>
                                <strong className="search-result-name">{user.fullName}</strong>
                                <span className={`search-result-role ${(user.role || 'unknown').toLowerCase()}`}>{(user.role || 'unknown').charAt(0).toUpperCase() + (user.role || 'unknown').slice(1)}</span>
                                <div className="search-result-details">{user.email} | {user.username}</div>
                            </div>
                            <button className="button-primary add-existing-button" onClick={() => handleAddExistingUser(user)} disabled={isSaving || existingUsers.some(emp => emp.uid === user.uid || emp.id === user.uid || emp.username === user.username)}>
                                {existingUsers.some(emp => emp.uid === user.uid || emp.id === user.uid || emp.username === user.username) ? 'Already Added' : 'Add'}
                            </button>
                        </div>
                    ))
                ) : (!isSearching && searchTerm.length > 0 && <p className="no-data-cell">No users found.</p>)}
            </div>
            <div className="modal-actions" style={{ justifyContent: 'space-between', marginTop: '20px' }}>
                <button type="button" className="button-secondary" onClick={closeModal} disabled={isSaving}>Cancel</button>
                <button type="button" className="button-primary" onClick={() => setView('create')}>+ Create New Employee</button>
            </div>
        </>
    );

    const renderCreateEditView = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', color: '#2d3748', fontWeight: '600', borderBottom: '2px solid #e2e8f0', paddingBottom: '6px' }}>
                    {isEditMode ? (
                        <>Edit Employee: <span style={{ color: '#0056b3' }}>{employeeToEdit?.username || ''}</span></>
                    ) : 'Create New Employee'}
                </h2>
                <button type="button" onClick={closeModal} disabled={isSaving} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: isSaving ? 'not-allowed' : 'pointer', color: isSaving ? '#cbd5e0' : '#a0aec0', padding: '0', lineHeight: '24px' }}>&times;</button>
            </div>
            {renderLimitWarning()}
            {!isEditMode && (
                <div style={{ backgroundColor: '#ebf8ff', border: '1px solid #90cdf4', color: '#2a4365', padding: '12px', borderRadius: '6px', marginBottom: '15px', fontSize: '13px' }}>
                    Same real email or phone can be used for a separate employee if the username is different. If the username already exists, the app will ask you to link that SSO identity instead.
                </div>
            )}
            <form>
                <div className="form-grid">
                    <div className="form-group"><label>Full Name</label><input name="fullName" value={formData.fullName} onChange={handleChange} required /></div>
                    {!isEditMode ? (
                        <div className="form-group"><label>Username</label><input name="username" value={formData.username} onChange={handleChange} required /></div>
                    ) : (
                        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px', color: '#a0aec0', backgroundColor: '#f7fafc', padding: '6px 10px', borderRadius: '8px', border: '1px solid #edf2f7' }}>
                                <span style={{ fontWeight: '600', color: '#718096' }}>UID: <span style={{ fontFamily: 'monospace', fontWeight: 'normal', color: '#4a5568' }}>{employeeToEdit?.uid || 'N/A'}</span></span>
                                <button type="button" onClick={() => {navigator.clipboard.writeText(employeeToEdit?.uid); alert('Copied UID!');}} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#718096', display: 'flex' }} title="Copy UID">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                </button>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '11px', color: '#a0aec0', backgroundColor: '#f7fafc', padding: '6px 10px', borderRadius: '8px', border: '1px solid #edf2f7' }}>
                                <span style={{ fontWeight: '600', color: '#718096' }}>DOC ID: <span style={{ fontFamily: 'monospace', fontWeight: 'normal', color: '#4a5568' }}>{employeeToEdit?.id || 'N/A'}</span></span>
                                <button type="button" onClick={() => {navigator.clipboard.writeText(employeeToEdit?.id); alert('Copied Doc ID!');}} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#718096', display: 'flex' }} title="Copy Document ID">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="form-group"><label>Email Address</label><input type="email" name="email" value={formData.email} onChange={handleChange} required /></div>
                    
                    {!isEditMode && (
                        <div className="form-group"><label>Password</label><input type="password" name="password" onChange={handleChange} required /></div>
                    )}

                    <div className="form-group">
                        <label>Job Title</label>
                        <select name="jobTitle" value={formData.jobTitle} onChange={handleChange} required>
                            <option value="">-- Select a Job Title --</option>
                            {JOB_TITLES.map(title => (<option key={title} value={title}>{title}</option>))}
                        </select>
                    </div>

                    <div className="form-group"><label>Primary Region</label><input name="primaryRegionalTag" value={formData.primaryRegionalTag} onChange={handleChange} placeholder="e.g. North London" /></div>
                    <div className="form-group"><label>Secondary Region</label><input name="secondaryRegionalTag" value={formData.secondaryRegionalTag} onChange={handleChange} placeholder="e.g. East London" /></div>

                    <div className="form-group">
                        <label>Phone Number</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <input 
                                type="text" 
                                name="phoneCode" 
                                value={formData.phoneCode} 
                                onChange={handleChange} 
                                placeholder="+44" 
                                style={{ width: '60px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} 
                            />
                            <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="7575944496" style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                        </div>
                    </div>
                    
                    <div className="form-group">
                        <label>Travel</label>
                        <select name="isTravelling" value={formData.isTravelling} onChange={handleChange} required>
                            <option value="">-- Select --</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                    </div>
                </div>
                
                <div style={{ display: 'flex', gap: '15px', marginTop: '1.5rem', width: '100%' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label>Assign to Admin</label>
                        <select name="assignedAdminId" value={formData.assignedAdminId} onChange={handleChange} required disabled={isLoadingAdmins}>
                            <option value="">{isLoadingAdmins ? 'Loading admins...' : '-- Select an Admin --'}</option>
                            {admins.map(admin => <option key={admin.id} value={admin.username}>{admin.username} ({admin.fullName})</option>)}
                        </select>
                    </div>

                    {isEditMode && (
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Security Options</label>
                            <button 
                                type="button" 
                                className="button-secondary"
                                onClick={handleSendResetEmail}
                                disabled={resetEmailSent}
                                style={{ width: '100%', height: '42px', marginTop: 'auto' }}
                            >
                                {resetEmailSent ? "Reset Email Sent ✓" : "Send Password Reset Email"}
                            </button>
                        </div>
                    )}
                </div>

                {apiError && <p className="error-message">{apiError}</p>}
                
                <div className="modal-actions" style={{ gap: '10px' }}>
                    <button type="button" className="button-secondary" onClick={isEditMode ? closeModal : () => setView('search')}>{isEditMode ? 'Cancel' : 'Back to Search'}</button>
                    <button type="button" className={isEditMode ? "button-primary" : "button-primary-outline"} disabled={isSaving} onClick={(e) => handleSubmit(e, false)}>
                        {isSaving ? <Spinner /> : (isEditMode ? 'Save Changes' : 'Quick Create')}
                    </button>
                    {!isEditMode && (
                        <button type="button" className="button-primary" disabled={isSaving} style={{ display: 'flex', alignItems: 'center', gap: '5px' }} onClick={(e) => handleSubmit(e, true)}>
                            {isSaving ? <Spinner /> : <>Create & Edit Profile <FaArrowRight /></>}
                        </button>
                    )}
                </div>
            </form>
        </>
    );

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                {view === 'search' ? renderSearchView() : renderCreateEditView()}
            </div>
        </div>
    );
};

export default AddEmployeeModal;